import { Selector } from "react-redux";
import { createSelector } from "reselect";

import { AggregationFunction, AggregationParams, IAggregationsParams } from "@multiweb/clients/dataReader";
import { isFetching } from "@multiweb/reactCore/fetching";
import { IAppState } from "@multiweb/types/react";
import { DEFAULT_FORMAT_GENERAL } from "./constants";
import {
    AggregationResult,
    AggregationRow,
    AggregationRowKey,
    Column,
    ColumnAggregationFunctions,
    ColumnKey,
    ColumnSelectorParams,
    ColumnType,
    Filter,
    IAggregationRowState,
    SelectorParams,
} from "./types";
import { createAggregationResultFetchingKey } from "./utils";

/**
 * Získá z globálního stavu podstrom patřící modulu agregačního řádku.
 * @param state
 */
const getModel: Selector<IAppState, IAggregationRowState> = (state) => state.aggregationRow;

/**
 * Získá podstrom stavu patřící konkrétnímu agregačnímu řádku.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku sloužící k jeho jednoznačné identifikaci.
 * @throws Vyhazuje chybu v případě, že agregační řádek se zadaným klíčem neexistuje.
 */
const getAggregationRow: Selector<IAppState, AggregationRow, AggregationRowKey> = (state, aggregationRowKey) => {
    const aggregationRowState = getModel(state).aggregationRows[aggregationRowKey];
    if (!aggregationRowState) {
        throw new Error(`Aggregation row with key ${aggregationRowKey} is not initialized.`);
    }
    return aggregationRowState;
};

/**
 * Získá sloupec agregačního řádku podle jeho klíče.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, ze kterého se sloupec získává.
 * @param columnKey Klíč získávaného sloupce.
 */
export const getColumn: Selector<IAppState, Column | undefined, ColumnSelectorParams> = (state, { aggregationRowKey, columnKey }) => getAggregationRow(state, aggregationRowKey).columns[columnKey];

/**
 * Získá pořadí sloupců pro vykreslení. Pořadí je definováno pořadím klíčů sloupců v poli.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, pro který se získává pořadí sloupců.
 */
export const getOrderOfColumns: Selector<IAppState, ReadonlyArray<ColumnKey>, SelectorParams> = (state, { aggregationRowKey }) => getAggregationRow(state, aggregationRowKey).orderOfColumns;

/**
 * Získá počet sloupců zmražených vůči horizontálnímu scrollování.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, pro který se získává počet zmražených sloupců.
 */
export const getNumberOfFrozenColumns: Selector<IAppState, number, SelectorParams> = (state, { aggregationRowKey }) => getAggregationRow(state, aggregationRowKey).numberOfFrozenColumns;

/**
 * Získá název třídy (entity), pro kterou se provádí agregace.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, pro který se získává název třídy.
 */
export const getClassName: Selector<IAppState, string | undefined, SelectorParams> = (state, { aggregationRowKey }) => getAggregationRow(state, aggregationRowKey).className ?? undefined;

/**
 * Získá filtr pro omezení agregovaných záznamů.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, pro který se získává filtr.
 */
export const getFilter: Selector<IAppState, Filter, SelectorParams> = (state, { aggregationRowKey }) => getAggregationRow(state, aggregationRowKey).filter;

/**
 * Získá šířku sloupce agregačního řádku podle jeho klíče.
 * @param state
 * @param params Objekt obsahující parametry.
 * @param params.aggregationRowKey Klíč agregačního řádku, ze kterého se sloupec získává.
 * @param params.columnKey Klíč získávaného sloupce.
 */
export const getColumnWidth: Selector<IAppState, number | undefined, ColumnSelectorParams> = (state, params) => getColumn(state, params)?.width;

/**
 * Získá typ sloupce agregačního řádku podle jeho klíče.
 * @param state
 * @param params Objekt obsahující parametry.
 * @param params.aggregationRowKey Klíč agregačního řádku, ze kterého se sloupec získává.
 * @param params.columnKey Klíč získávaného sloupce.
 * @see ColumnType
 */
export const getColumnType: Selector<IAppState, ColumnType | undefined, ColumnSelectorParams> = (state, params) => getColumn(state, params)?.columnType;

/**
 * Získá vybranou agregační funkci pro sloupce agregačního řádku podle jeho klíče.
 * @param state
 * @param params Objekt obsahující parametry.
 * @param params.aggregationRowKey Klíč agregačního řádku, ze kterého se sloupec získává.
 * @param params.columnKey Klíč získávaného sloupce.
 */
export const getColumnAggregationFunction: Selector<IAppState, AggregationFunction | undefined, ColumnSelectorParams> = (state, params) => getColumn(state, params)?.aggregationFunction ?? undefined;

/**
 * Získá výsledek agregační funkce pro sloupec agregačního řádku podle jeho klíče. Výsledek je již ve formě naformátovaného řetězce.
 * @param state
 * @param params Objekt obsahující parametry.
 * @param params.aggregationRowKey Klíč agregačního řádku, ze kterého se sloupec získává.
 * @param params.columnKey Klíč získávaného sloupce.
 */
export const getColumnAggregationResult: Selector<IAppState, AggregationResult | undefined, ColumnSelectorParams> = (state, params) => getColumn(state, params)?.aggregationResult ?? undefined;

/**
 * Získá název atributu, který sloupec zobrazuje.
 * @param state
 * @param params Objekt obsahující parametry.
 * @param params.aggregationRowKey Klíč agregačního řádku, ze kterého se sloupec získává.
 * @param params.columnKey Klíč získávaného sloupce.
 */
export const getColumnAttributeName: Selector<IAppState, string | undefined, ColumnSelectorParams> = (state, params) => getColumn(state, params)?.attributeName ?? undefined;

/**
 * Získá formátovací řetězec pro sloupec agregačního řádku podle jeho klíče.
 * @param state
 * @param params Objekt obsahující parametry.
 * @param params.aggregationRowKey Klíč agregačního řádku, ze kterého se sloupec získává.
 * @param params.columnKey Klíč získávaného sloupce.
 */
export const getColumnFormat: Selector<IAppState, string, ColumnSelectorParams> = (state, params) => getColumn(state, params)?.format ?? DEFAULT_FORMAT_GENERAL;

/**
 * Vytvoří selector pro získání klíčů sloupců se zmraženým horizontálním scrollováním v pořadí jejich vykreslení.
 */
export const createGetOrderedFrozenColumnKeys = () => createSelector<IAppState, SelectorParams, ReturnType<typeof getNumberOfFrozenColumns>, ReturnType<typeof getOrderOfColumns>, ReadonlyArray<ColumnKey>>(
    (state, params) => getNumberOfFrozenColumns(state, params),
    (state, params) => getOrderOfColumns(state, params),
    (numberOfFrozenColumns, orderOfColumns) => orderOfColumns.slice(0, numberOfFrozenColumns),
);

/**
 * Vytvoří selector pro získání klíčů sloupců, které nemají zmražené horizontální scrollování, v pořadí jejich vykreslení.
 */
export const createGetOrderedNonFrozenColumnKeys = () => createSelector<IAppState, SelectorParams, ReturnType<typeof getNumberOfFrozenColumns>, ReturnType<typeof getOrderOfColumns>, ReadonlyArray<ColumnKey>>(
    (state, params) => getNumberOfFrozenColumns(state, params),
    (state, params) => getOrderOfColumns(state, params),
    (numberOfFrozenColumns, orderOfColumns) => orderOfColumns.slice(numberOfFrozenColumns),
);

/**
 * Zjistí, zda probíhá načítání výsledku agregační funkce pro sloupec se zadaným klíčem.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, na jehož sloupec se dotazujeme.
 * @param columnKey Klíč sloupce, na jehož načítání se dotazujeme.
 */
export const isColumnResultLoading: Selector<IAppState, boolean, ColumnSelectorParams> = (state, { aggregationRowKey, columnKey }) =>
    isFetching(state, createAggregationResultFetchingKey(aggregationRowKey)) || // načítá se pro všechny sloupce agregačního řádku
    isFetching(state, createAggregationResultFetchingKey(aggregationRowKey, columnKey)); // načítá se dotazovaný sloupec

/**
 * Získá vstup pro hromadné načtení výsledků pro agregační funkce numerických řádků. Není použitý reselect, protože selector je určený pouze pro ságu a nebude se
 * volat příliš často.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, pro který se mají získat vstupy k načtení výsledků.
 */
export const unsafeGetColumnsForReload: Selector<IAppState, IAggregationsParams, SelectorParams> = (state, { aggregationRowKey }) => Object.values(getAggregationRow(state, aggregationRowKey).columns)
    .filter(({ columnType, aggregationFunction }) => columnType === ColumnType.numeric && aggregationFunction)
    .reduce((result, { key, aggregationFunction, attributeName }) => ({
        ...result,
        [key]: {
            attributeName,
            aggregationFunction,
        } as AggregationParams,
    }), {} as IAggregationsParams);

/**
 * Získá objekt obsahující zvolené agregační funkce pod klíči sloupců. Není použitý reselect, protože selector je určený pouze pro ságu a nebude se
 * volat příliš často.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, na jehož sloupec se dotazujeme.
 */
export const unsafeGetColumnsAggregationFunctions: Selector<IAppState, ColumnAggregationFunctions, SelectorParams> = (state, { aggregationRowKey }) => (
    Object.values(getAggregationRow(state, aggregationRowKey).columns)
        .filter(({ columnType }) => columnType === ColumnType.numeric)
        .reduce((result, { key, aggregationFunction }) => ({
            ...result,
            [key]:  aggregationFunction,
        }), {} as ColumnAggregationFunctions)
);

/**
 * Získá objekt obsahující všechny zvolené agregační funkce pod klíči sloupců. Není použitý reselect, protože selector je určený pouze pro ságu a nebude se
 * volat příliš často.
 * @param state
 * @param aggregationRowKey Klíč agregačního řádku, na jehož sloupec se dotazujeme.
 */
export const unsafeGetAllColumnsAggregationFunctions: Selector<IAppState, ColumnAggregationFunctions, SelectorParams> = (state, { aggregationRowKey }) => (
    Object.values(getAggregationRow(state, aggregationRowKey).columns)
        .reduce((result, { key, aggregationFunction }) => ({
            ...result,
            [key]:  aggregationFunction,
        }), {} as ColumnAggregationFunctions)
);
